import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Lead, ChecklistItem } from "@/types";
import { useChecklist } from "@/hooks/useChecklist";
import { Loader2, CheckCircle2, AlertCircle, Lock, FileText } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useModal } from "@/contexts/ModalContext";

interface ChecklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onSave: (checklist: ChecklistItem[]) => void;
}

export function ChecklistDialog({ open, onOpenChange, lead }: ChecklistDialogProps) {
  const { registerModal, unregisterModal } = useModal();

  useEffect(() => {
    if (!open) return;
    registerModal('lead-checklist-dialog', () => onOpenChange(false), 1);
    return () => unregisterModal('lead-checklist-dialog');
  }, [open, onOpenChange, registerModal, unregisterModal]);

  const {
    checklist,
    response,
    isLoading,
    error,
    isVersionLocked,
    stats,
    categoryStats,
    toggleItem,
    addNote,
    isSaving,
  } = useChecklist(lead.loanType, lead.lender, { leadId: lead.id });

  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");

  const handleToggle = async (itemId: string) => {
    await toggleItem(itemId);
  };

  const openNoteDialog = (itemId: string) => {
    setSelectedItemId(itemId);
    const existingNote = response?.notes?.[itemId] || "";
    setNoteText(existingNote);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (selectedItemId) {
      await addNote(selectedItemId, noteText);
      setNoteDialogOpen(false);
      setSelectedItemId(null);
      setNoteText("");
    }
  };

  const isItemChecked = (itemId: string) => {
    return response?.checkedItems.includes(itemId) || false;
  };

  const getItemNote = (itemId: string) => {
    return response?.notes?.[itemId];
  };

  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error Loading Checklist
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open && !noteDialogOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <DialogTitle className="text-xl">
                  {checklist?.name || "Loan Processing Checklist"}
                </DialogTitle>
                {checklist?.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {checklist.description}
                  </p>
                )}
              </div>
              {isVersionLocked && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  v{response?.formVersion}
                </Badge>
              )}
            </div>
          </DialogHeader>

          <div className="py-2 flex-1 overflow-hidden flex flex-col">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !checklist ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-sm text-muted-foreground">No checklist available</p>
              </div>
            ) : (
              <>
                {/* Overall Progress */}
                <div className="mb-4 p-4 rounded-xl bg-secondary/50 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Overall Progress</span>
                    <span className="text-lg font-bold">{stats?.progress || 0}%</span>
                  </div>
                  <div className="h-2.5 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all duration-300"
                      style={{ width: `${stats?.progress || 0}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>
                      {stats?.checkedItems || 0} of {stats?.totalItems || 0} completed
                    </span>
                    {stats && stats.requiredChecked < stats.requiredItems && (
                      <span className="text-orange-500 font-medium">
                        {stats.requiredItems - stats.requiredChecked} required remaining
                      </span>
                    )}
                  </div>
                </div>

                {/* Categories with Accordion */}
                <Accordion
                  type="multiple"
                  defaultValue={checklist.categories.map(cat => cat.id)}
                  className="flex-1 overflow-y-auto space-y-2"
                >
                  {checklist.categories.map((category) => {
                    const catStats = categoryStats.find(cs => cs.categoryId === category.id);
                    return (
                      <AccordionItem
                        key={category.id}
                        value={category.id}
                        className="border rounded-xl px-4 bg-card"
                      >
                        <AccordionTrigger className="hover:no-underline py-3">
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="flex items-center gap-3">
                              <div>
                                <h3 className="font-medium text-left">{category.name}</h3>
                                {category.description && (
                                  <p className="text-xs text-muted-foreground text-left mt-0.5">
                                    {category.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {catStats && catStats.checkedItems === catStats.totalItems && (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              )}
                              <span className="text-xs font-medium">
                                {catStats?.checkedItems || 0}/{catStats?.totalItems || 0}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-4">
                          {/* Category Progress Bar */}
                          <div className="mb-3 px-1">
                            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                              <div
                                className="h-full bg-accent transition-all duration-300"
                                style={{ width: `${catStats?.progress || 0}%` }}
                              />
                            </div>
                          </div>

                          {/* Category Items */}
                          <div className="space-y-2">
                            {category.items.map((item) => {
                              const checked = isItemChecked(item.id);
                              const note = getItemNote(item.id);
                              return (
                                <div
                                  key={item.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors border ${
                                    checked ? 'bg-secondary/30' : ''
                                  }`}
                                >
                                  <Checkbox
                                    id={item.id}
                                    checked={checked}
                                    onCheckedChange={() => handleToggle(item.id)}
                                    disabled={isSaving}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <label
                                        htmlFor={item.id}
                                        className={`text-sm cursor-pointer flex items-center gap-2 ${
                                          checked ? 'line-through text-muted-foreground' : ''
                                        }`}
                                      >
                                        {item.label}
                                        {item.isRequired && (
                                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                            Required
                                          </Badge>
                                        )}
                                      </label>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openNoteDialog(item.id)}
                                        className="h-7 w-7 p-0 flex-shrink-0"
                                      >
                                        <FileText className={`h-3.5 w-3.5 ${note ? 'text-accent' : ''}`} />
                                      </Button>
                                    </div>
                                    {item.description && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {item.description}
                                      </p>
                                    )}
                                    {item.helpText && (
                                      <HoverCard>
                                        <HoverCardTrigger asChild>
                                          <button className="text-xs text-blue-500 hover:underline mt-1">
                                            Need help?
                                          </button>
                                        </HoverCardTrigger>
                                        <HoverCardContent className="w-80">
                                          <p className="text-sm">{item.helpText}</p>
                                        </HoverCardContent>
                                      </HoverCard>
                                    )}
                                    {note && (
                                      <div className="mt-2 p-2 bg-secondary/50 rounded text-xs">
                                        <span className="font-medium">Note:</span> {note}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </>
            )}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-xs text-muted-foreground">
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              ) : (
                <span>Changes auto-saved</span>
              )}
            </div>
            <Button onClick={() => onOpenChange(false)} className="rounded-xl">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
              className="resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setNoteDialogOpen(false);
                setNoteText("");
                setSelectedItemId(null);
              }}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button onClick={handleSaveNote} disabled={isSaving} className="rounded-xl">
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Note'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
